import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../exam.js';
import { Student } from '../student.js';

/**
 * Whether the given exam's owner has a Student profile — i.e. it's a student's own self-upload
 * rather than a teacher grading a class. Same "whichever profile exists for this userID"
 * resolution as useCurrentUserProfile.ts, keyed on the exam owner instead of the current session
 * user (several call sites here run in a background job, with no session to read).
 */
export async function resolveIsStudentOwnedExam(examId: string): Promise<boolean> {
  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId }, { projection: { userID: 1 } });
    const userID = (exam as any)?.userID;
    if (!userID) return false;

    const studentCollection = await getCollection(Student);
    const studentDoc = await studentCollection.findOne({ userID });
    return Boolean(studentDoc);
  } catch (err) {
    logger.error({ err, examId }, 'Failed to resolve whether exam owner is a student');
    return false;
  }
}

/**
 * Whether the CURRENT SESSION (not an exam's owner — see `resolveIsStudentOwnedExam` above) has a Student
 * profile. Same "whichever profile exists for this userID" resolution, keyed on the signed-in user instead
 * — used to gate a feature to student accounts only, e.g. the online student-exam submission flow, which is
 * a student-app-only feature a teacher previewing their own exam should not be able to use as if they were
 * the student answering it.
 */
export async function resolveIsCurrentSessionStudent(userID: string): Promise<boolean> {
  try {
    if (!userID) return false;
    const studentCollection = await getCollection(Student);
    const studentDoc = await studentCollection.findOne({ userID });
    return Boolean(studentDoc);
  } catch (err) {
    logger.error({ err, userID }, 'Failed to resolve whether the current session is a student');
    return false;
  }
}
